
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import * as ag_cli from 'ag-client-typescript';

import {
  ag_test_case_result_correctness,
  CorrectnessLevel
} from '@/components/project_view/submission_detail/correctness';
import { handle_global_errors_async } from '@/error_handling';
import { SafeMap } from '@/safe_map';

import BugsExposedHistogram from './bugs_exposed_histogram.vue';
import DescriptiveStatsTable from './descriptive_stats_table.vue';

@Component({
  components: {
    BugsExposedHistogram,
    DescriptiveStatsTable
  }
})
export default class PassFailCounts extends Vue {
  @Prop({required: true, type: ag_cli.Project})
  project!: ag_cli.Project;

  @Prop({required: true})
  ultimate_submission_entries!: ag_cli.FullUltimateSubmissionResult[];

  d_ag_test_suites: ag_cli.AGTestSuite[] = [];
  d_mutation_suites: ag_cli.MutationTestSuite[] = [];

  d_loading = true;

  // <test case pk>: <num pass>
  d_pass_counts = new SafeMap<number, number>();
  // <test case pk>: <num fail>
  d_fail_counts = new SafeMap<number, number>();

  get submission_results(): ag_cli.SubmissionResultFeedback[] {
    return this.ultimate_submission_entries.reduce(
      (so_far, entry) => {
        if (entry.ultimate_submission !== null) {
          so_far.push(entry.ultimate_submission.results);
        }
        return so_far;
      },
      [] as ag_cli.SubmissionResultFeedback[]
    );
  }

  @handle_global_errors_async
  async created() {
    await this.load_data();
    this.d_loading = false;
  }

  @Watch('ultimate_submission_entries')
  on_ultimate_submission_entries_change() {
    return this.load_data();
  }

  async load_data() {
    [this.d_ag_test_suites, this.d_mutation_suites] = await Promise.all([
      ag_cli.AGTestSuite.get_all_from_project(this.project.pk),
      ag_cli.MutationTestSuite.get_all_from_project(this.project.pk)
    ]);

    let pass_counts = new SafeMap<number, number>();
    let fail_counts = new SafeMap<number, number>();

    for (let result of this.submission_results) {
      for (let suite_result of result.ag_test_suite_results) {
        for (let test_case_result of suite_result.ag_test_case_results) {
          let correctness = ag_test_case_result_correctness(test_case_result);
          // Correctness level should never be "not_available"
          if (correctness === CorrectnessLevel.info_only) {
            continue;
          }

          let counts = correctness === CorrectnessLevel.all_correct ? pass_counts : fail_counts;
          counts.set(
            test_case_result.ag_test_case_pk,
            counts.get(test_case_result.ag_test_case_pk, 0) + 1
          );
        }
      }
    }

    this.d_pass_counts = pass_counts;
    this.d_fail_counts = fail_counts;
  }

  get bugs_exposed_per_student_per_mutation_suite() {
    // Map of (suite name) -> (list of number of bugs exposed, one item per student)
    let result_lists = new SafeMap<string, number[]>();

    for (let suite of this.d_mutation_suites) {
      result_lists.set(suite.name, []);
    }

    for (let result of this.submission_results) {
      for (let suite_result of result.mutation_test_suite_results) {
        result_lists.get(
          suite_result.mutation_test_suite_name
        ).push(suite_result.num_bugs_exposed ?? 0);
      }
    }

    return result_lists;
  }
}
